import { Button, CallbackExtraFieldsType, Grid, Heading, InputEmailPhone, InputOnChange, Link, Theme, useIsTheme } from '@afterpaytouch/core'
import React, { FormEvent, FunctionComponent, useEffect, useState } from 'react'
import { isValidEmail } from '../../utils/validation'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useConsumerSizes } from '../../utils/responsive'
import { useEmailLookupMutation } from '../../state/account'
import { isUserLookupResponseType } from '@afterpaytouch/portal-api/consumer/lookup'
import { Route } from '../../config/router'
import { ErrorCode, isPortalApiError } from '@afterpaytouch/portal-api'
import { useCashWebLoginInitMutation } from '@afterpaytouch/portal-api/consumer/auth/cash'
import { getCashAppAppletUrl, getPortalUrlViaCountry } from '../../utils/redirects'
import { LaunchDarklyFlags, useFlag } from '../../hooks'
import { clearCookie } from '../../state/actions'
import { useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { TrackingEvent } from '../../model/amplitude'
import { getCountryCodeFromLocale } from '../../utils/consumer'
import { updateConsumerEmail } from '../../state/account/consumer/reducer'
import { resetAuthError, useAppDispatch, useAuthError, useCheckEmailEligibilityMutation, useConsumerEmail } from '../../state'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { useExperiment } from '../../state/experiments/hooks'
import { getErrorMessage, renderError, authErrorToErrorCode } from '../LoginPassword/errorUtils'

export const LoginIdentity: FunctionComponent = () => {
  const { t } = useTranslation()
  const router = useRouter()
  const { locale } = router
  const extractedCountryCodeFromLocale = getCountryCodeFromLocale(locale)
  const authError = useAuthError()
  const [error, setError] = useState(false)
  const [errorText, setErrorText] = useState<React.ReactNode>('')
  const [lock, setLocked] = useState(false)
  const { textSize } = useConsumerSizes()
  const testNameSpace = 'login-identity'
  const consumerEmail = useConsumerEmail()
  const [userIdentityValue, setUserIdentityValue] = useState<string>('')
  const [emailLookup, { isLoading }] = useEmailLookupMutation()
  const [cashWebLoginInit, { isLoading: isCashWebLoginInitLoading }] = useCashWebLoginInitMutation()
  const [checkEmailEligibility] = useCheckEmailEligibilityMutation()
  const isCashWebLoginEnabled = useFlag(LaunchDarklyFlags.EnablePortalCashWebLogin)
  const isCashTheme = useIsTheme(Theme.Cash)
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const [hasLoggedTypeEmailEvent, setHasLoggedTypeEmailEvent] = useState(false)
  const dispatch = useAppDispatch()
  // Use the new experiment system instead of simple flag
  const portalSignupExperiment = useExperiment('EnabledPortalSignup')
  const isPortalSignupEnabled = portalSignupExperiment.active && !portalSignupExperiment.control

  useEffect(() => {
    if (authError) {
      const errorCode = authErrorToErrorCode(authError)
      setErrorText(renderError(getErrorMessage(errorCode, 'login'), t))
    }
  }, [authError, t])

  const onEmailChange = (e: InputOnChange, { rawValue = '' }: CallbackExtraFieldsType): void => {
    const value: string = e.target.value ?? ''
    if (value !== '') {
      dispatch(resetAuthError())
      setErrorText('')
      setError(false)
      if (!hasLoggedTypeEmailEvent) {
        logEvent(TrackingEvent.TYPED_EMAIL_ON_LOGIN_PAGE)
        setHasLoggedTypeEmailEvent(true)
      }
    }
    setUserIdentityValue(rawValue)
  }

  const validateForm = (): boolean => {
    if (isValidEmail(userIdentityValue ?? '')) {
      return true
    }
    if (userIdentityValue === '') {
      setErrorText(renderError('login:error:emailOrMobileIsRequired', t))
    } else {
      setErrorText(renderError('login:error:emailNotValid', t))
    }
    return false
  }
  const handleEmailLookupError = (code: ErrorCode): void => {
    setErrorText(renderError(getErrorMessage(code, 'login'), t))
  }
  const handleCashWebLoginInitError = (): void => {
    setErrorText(renderError('login:error.cashWebLoginUnavailable', t))
  }

  const doEmailLookup = async (): Promise<void> => {
    try {
      const result = await emailLookup({ email: userIdentityValue, isFromRegistration: false }).unwrap()
      const isUserLookupResponse = isUserLookupResponseType(result)
      logEvent(TrackingEvent.USER_LOOK_UP_ON_LOGIN_PAGE, {
        success: !('error' in result),
        existing: result?.existing ?? undefined,
        passwordExists: result?.passwordExists ?? undefined,
      })
      if (isUserLookupResponse) {
        const existing = result.existing
        if (existing) {
          const countryCode = result.countryCode
          const passwordExists = result.passwordExists
          if (countryCode !== extractedCountryCodeFromLocale) {
            logEvent(TrackingEvent.CROSS_REGION_USER_REDIRECT_ON_LOGIN_PAGE, {
              from: extractedCountryCodeFromLocale,
              to: countryCode,
            })
            clearCookie()
            window.location.href = getPortalUrlViaCountry(countryCode)
            return
          }
          dispatch(updateConsumerEmail(userIdentityValue ?? ''))
          if (passwordExists) {
            await router.push(Route.LOGIN_PASSWORD)
          } else {
            try {
              await checkEmailEligibility({ email: userIdentityValue })
            } catch {
              // proceed to verify even if eligibility check fails
            }
            await router.push(Route.CREATE_PASSWORD_VERIFY)
          }
        } else if (valueIsNotNullOrUndefined(result.shadowAccountExists) && result.shadowAccountExists) {
          if (isCashWebLoginEnabled) {
            dispatch(updateConsumerEmail(userIdentityValue ?? ''))
            try {
              const cashLogin = await cashWebLoginInit({ email: userIdentityValue }).unwrap()
              logEvent(TrackingEvent.INITIATED_CASH_WEB_LOGIN)
              window.location.href = cashLogin.cashLoginUrl
            } catch {
              handleCashWebLoginInitError()
            }
          } else {
            const cashLinkComponent = (
              <Link
                color='Fire'
                key='description-link'
                onClick={() => logEvent(TrackingEvent.PRESSED_PORTAL_LOGIN_OPEN_CASH_APP_LINK)}
                target='_blank'
                kind='Underline'
                href={getCashAppAppletUrl()}
                testNameSpace={testNameSpace}
              />
            )

            setErrorText(
              renderError('login:error.existingEmailCashAccount', t, {
                cashLink: cashLinkComponent,
              })
            )
            logEvent(TrackingEvent.VIEWED_ATTEMPTED_LOGIN_WITH_CASH_EMAIL_ERROR)
          }
        } else {
          if (isPortalSignupEnabled) {
            // Account not found - navigate to signup if enabled
            // Log experiment variation for analysis
            logEvent(TrackingEvent.USER_LOOK_UP_ON_LOGIN_PAGE, {
              experimentVariation: portalSignupExperiment.value,
              experimentActive: portalSignupExperiment.active,
              experimentControl: portalSignupExperiment.control,
            })
            dispatch(updateConsumerEmail(userIdentityValue ?? ''))
            await router.push(Route.SIGNUP)
          } else {
            setErrorText(renderError('login:error:accountNotFound', t))
          }
        }
      }
    } catch (e) {
      const code = isPortalApiError(e.data) ? e.data.errorCode : ErrorCode.Error
      logEvent(TrackingEvent.USER_LOOK_UP_ON_LOGIN_PAGE, {
        success: false,
        error: e,
        errorCode: code,
      })
      handleEmailLookupError(code)
    }
  }

  const onFormSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const isValid = validateForm()
    logEvent(TrackingEvent.PRESSED_CONTINUE_ON_LOGIN_PAGE, { isValid })

    if (!isValid) {
      return
    }
    setLocked(true)
    await doEmailLookup()
    setLocked(false)
  }

  const onEmailBlur = (e: React.FocusEvent<HTMLInputElement>, { rawValue = '' }: CallbackExtraFieldsType): void => {
    setUserIdentityValue(rawValue ?? '')
  }

  return (
    <>
      <form onSubmit={onFormSubmit} className='mt-4' noValidate={true} {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-form` })}>
        {!isCashTheme && (
          <Grid>
            <Heading size={textSize} id='loginEmailLabel'>
              {t('login:label')}
            </Heading>
          </Grid>
        )}
        <Grid>
          <MaskedElement stylingModifier='wrap' actionName='Mobile/Email Input'>
            <InputEmailPhone
              defaultValue={consumerEmail ?? ''}
              enableInputPhoneMode={false}
              label={t('login:label')}
              onChange={onEmailChange}
              onBlur={onEmailBlur}
              name='identifier'
              testNameSpace={testNameSpace}
              required={true}
              autoComplete='username'
              error={error || Boolean(errorText)}
              errorText={errorText}
              disabled={lock || isLoading || isCashWebLoginInitLoading}
              aria-labelledby='loginEmailLabel'
              autoFocus={true}
              autoSelectOnFocus={true}
            />
          </MaskedElement>
        </Grid>
        <Grid>
          <Grid.Item>
            <Button.Primary loading={lock} type='submit' testNameSpace={testNameSpace} disabled={lock || isCashWebLoginInitLoading} padding='Fluid'>
              {t('common:continue')}
            </Button.Primary>
          </Grid.Item>
        </Grid>
      </form>
    </>
  )
}
